import { OrderType } from '@katanaperps/katana-perps-sdk';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import { getMarginRatio } from '#/api/apiUtils';
import { ApiOrder, ApiPosition } from '#/api/types';
import DataPair from '#/components/basic/panels/DataPair';
import { useActiveWindowInterval } from '#/components/hooks';
import { fetchOrdersFromDisplayedOrderbook } from '#/components/orders/orderBook/orderBookService';
import {
  buildMarketPair,
  formatNumber,
  formatNumber2,
  formatUsdWithSign,
} from '#/lib/display/formatting';
import { truncateNumberBasedOnMultiplier } from '#/lib/numbers';
import { capitalizeFirstLetter } from '#/lib/string';
import {
  useSelectMarketData,
  useSelectMarketStepSize,
} from '#/reduxState/markets/listed/listedMarketsUtils';
import { useSelector } from '#/reduxState/storeCore';

import { PanelState } from './buyPanelReducer';
import { TwoColumnData, TwoColumnDataSimple } from './layout';
import {
  calculateExecutionQuantities,
  calculateReduceOnlyRestrictions,
} from './orderCalculations';

function useCalculateQuantities({
  enabled,
  amount,
  amountInQuote,
  initialMarginFraction,
  stepSizeDecimals,
  stepSizeAsDecimal,
  limitPrice,
  maxBuyQuantity,
  maxSellQuantity,
  ignoreOrderbook,
}: {
  enabled: boolean;
  amount: string | undefined;
  amountInQuote: boolean;
  initialMarginFraction: string | undefined;
  stepSizeDecimals?: number;
  stepSizeAsDecimal?: string;
  limitPrice?: string;
  maxBuyQuantity: string | boolean;
  maxSellQuantity: string | boolean;
  ignoreOrderbook: boolean;
}) {
  const [calculated, setCalculated] = useState<
    | {
        /** We still show previous values, but we inform user about pending recalculation */
        revalidating?: boolean;
        baseQuantityBuy: string;
        baseQuantitySell: string;
        quoteQuantityBuy: string;
        quoteQuantitySell: string;
      }
    | undefined
  >(undefined);

  const fetchingBaseQuantityRef = useRef(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCalculated(state =>
      state ? { ...state, revalidating: true } : undefined
    );
  }, [amount, limitPrice]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCalculated(undefined);
  }, [amountInQuote]);

  useEffect(() => {
    if (!amount || !enabled || !initialMarginFraction) {
      if (calculated) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setCalculated(undefined);
      }
      return;
    }
  }, [amount, calculated, enabled, initialMarginFraction]);

  const calculateQuantities = useCallback(() => {
    if (
      stepSizeDecimals === undefined ||
      !amount ||
      !enabled ||
      !initialMarginFraction
    ) {
      return;
    }
    if (fetchingBaseQuantityRef.current === true) {
      return;
    }
    fetchingBaseQuantityRef.current = true;
    if (!initialMarginFraction) {
      throw new Error('initialMarginFraction available');
    }
    if (stepSizeAsDecimal === undefined) {
      throw new Error('Step size not available');
    }
    const calculateExecution = async (amountInBaseOverride?: string) => {
      const orderbookState = ignoreOrderbook
        ? undefined
        : await fetchOrdersFromDisplayedOrderbook();
      if (!ignoreOrderbook && !orderbookState) {
        throw new Error('Orderbook not available');
      }
      return calculateExecutionQuantities({
        amount: amountInBaseOverride ?? amount,
        amountInQuote: amountInBaseOverride ? false : amountInQuote,
        marketData: { initialMarginFraction },
        orderbookState,
        stepSizeDecimals,
        limitPrice,
      });
    };
    calculateExecution()
      .then(async calculatedInitial => {
        let calculatedBuy = calculatedInitial;
        let calculatedSell = calculatedInitial;
        // Check if values aren't out of bounds
        if (
          typeof maxBuyQuantity === 'string' &&
          Number(calculatedInitial.baseQuantityBuy) > Number(maxBuyQuantity)
        ) {
          calculatedBuy = await calculateExecution(maxBuyQuantity);
        }
        if (
          typeof maxSellQuantity === 'string' &&
          Number(calculatedInitial.baseQuantitySell) > Number(maxSellQuantity)
        ) {
          calculatedSell = await calculateExecution(maxSellQuantity);
        }
        setCalculated({
          baseQuantityBuy: truncateNumberBasedOnMultiplier(
            calculatedBuy.baseQuantityBuy,
            stepSizeAsDecimal
          ),
          baseQuantitySell: truncateNumberBasedOnMultiplier(
            calculatedSell.baseQuantitySell,
            stepSizeAsDecimal
          ),
          quoteQuantityBuy: calculatedBuy.quoteQuantityBuy,
          quoteQuantitySell: calculatedSell.quoteQuantitySell,
        });
      })
      .finally(() => {
        fetchingBaseQuantityRef.current = false;
      });
  }, [
    amount,
    amountInQuote,
    enabled,
    ignoreOrderbook,
    limitPrice,
    initialMarginFraction,
    maxBuyQuantity,
    maxSellQuantity,
    stepSizeAsDecimal,
    stepSizeDecimals,
  ]);

  useEffect(() => {
    calculateQuantities();
  }, [calculateQuantities]);

  useActiveWindowInterval(() => {
    calculateQuantities();
  }, 2000);

  return calculated;
}

export type BuySellPanelCalculatedInfo = {
  revalidating: boolean;
  /** Disabled for specific types like Market Stop orders */
  notAvailable: boolean;
  costBuy?: number;
  costSell?: number;
  quantityBuy?: string;
  quantitySell?: string;
  valueBuy?: string;
  valueSell?: string;
};

type Props = {
  state: PanelState;
  token1Base?: string;
  token2Quote?: string;
  buyingPower: number | undefined;
  /** `false` if no position open, undefined = not initialized */
  openPosition?: ApiPosition | false;
  walletOrders: ApiOrder[] | undefined;
  initialMarginFractionOverride: string | null | undefined;
};

export const useBuySellCalculatedInfo = ({
  state,
  buyingPower,
  token1Base,
  token2Quote,
  openPosition,
  walletOrders,
  initialMarginFractionOverride,
}: Props): BuySellPanelCalculatedInfo => {
  const marketData = useSelectMarketData(
    buildMarketPair(token1Base, token2Quote)
  );
  const stepSize = useSelectMarketStepSize(marketData?.market);

  const {
    size,
    sizeInQuote,
    sizePercentage: sizePercentageUncorrected,
    orderType,
    price,
    triggerEnabled,
    reduceOnly,
  } = state;

  const isStopLimit = orderType === OrderType.limit && state.triggerEnabled;
  const isStopMarket = orderType === OrderType.market && state.triggerEnabled;

  const openPositionQty = openPosition ? Number(openPosition.quantity) : 0;

  // To prevent failing orders, we cap percentage. Reduce-only closes a
  // fraction of an existing position so the 97% safety cap doesn't apply —
  // closing the whole position is valid.
  const sizePercentage =
    reduceOnly && openPositionQty
      ? sizePercentageUncorrected
      : Math.min(sizePercentageUncorrected, 97);

  // For Slider set value, we have to recalculate using buyingPower
  const rate = sizePercentage / 100;

  // In reduce-only mode, the slider should map to a fraction of the open
  // position (close X% of position), not a fraction of buyingPower. We pass
  // that as a base-quantity amount so the calculator caps appropriately.
  const reduceOnlySliderQty =
    sizePercentage && reduceOnly && openPositionQty
      ? Math.abs(openPositionQty) * rate
      : 0;

  const amountNotSanitized = sizePercentage
    ? reduceOnlySliderQty
      ? reduceOnlySliderQty.toString()
      : ((buyingPower || 0) * rate).toString()
    : size;

  // Note: Slider (%) orders are always in "Quote"
  const amountInQuote = sizePercentage ? !reduceOnlySliderQty : sizeInQuote;

  const amount = useMemo(() => {
    if (amountInQuote) {
      return amountNotSanitized;
    }
    if (stepSize?.asDecimal === undefined) {
      console.warn('Step size not defined');
      return '0';
    }
    return amountNotSanitized.trim()
      ? truncateNumberBasedOnMultiplier(amountNotSanitized, stepSize.asDecimal)
      : undefined;
  }, [amountInQuote, amountNotSanitized, stepSize]);

  const marginRatio = getMarginRatio(
    marketData?.initialMarginFraction,
    initialMarginFractionOverride
  );

  const { maxBuyQuantity, maxSellQuantity } = calculateReduceOnlyRestrictions({
    reduceOnly,
    openPosition,
    openOrders: walletOrders,
    limitPrice: state.triggerEnabled ? false : state.price,
    triggerEnabled: state.triggerEnabled,
  });
  const calculatedQuantities = useCalculateQuantities({
    enabled:
      !isStopMarket &&
      !!marginRatio &&
      state.state !== 'successBuy' &&
      state.state !== 'successSell' &&
      (orderType === OrderType.market ? true : !!price),
    amount,
    amountInQuote,
    initialMarginFraction: marginRatio,
    stepSizeDecimals: stepSize?.visibleDecimals,
    stepSizeAsDecimal: stepSize?.asDecimal,
    limitPrice: orderType === OrderType.market ? undefined : price,
    maxBuyQuantity,
    maxSellQuantity,
    // For Stop limit orders and orders with PostOnly we skip orderbook introspection
    ignoreOrderbook: isStopLimit || state.postOnly,
  });

  const revalidating = !!calculatedQuantities?.revalidating;

  if (triggerEnabled && orderType === OrderType.market) {
    return {
      revalidating: false,
      notAvailable: triggerEnabled && orderType == OrderType.market,
      costBuy: undefined,
      costSell: undefined,
      quantityBuy: amount,
      quantitySell: amount,
      valueBuy: '',
      valueSell: '',
    };
  }

  const costBuy =
    calculatedQuantities && marginRatio
      ? Number(calculatedQuantities.quoteQuantityBuy) * Number(marginRatio)
      : undefined;
  const costSell =
    calculatedQuantities && marginRatio
      ? Number(calculatedQuantities.quoteQuantitySell) * Number(marginRatio)
      : undefined;
  return {
    revalidating,
    notAvailable: false,
    costBuy,
    costSell,
    quantityBuy: calculatedQuantities?.baseQuantityBuy,
    quantitySell: calculatedQuantities?.baseQuantitySell,
    valueBuy: calculatedQuantities?.quoteQuantityBuy,
    valueSell: calculatedQuantities?.quoteQuantitySell,
  };
};

const formatCalculatedQuantity = (
  calculatedInfo: BuySellPanelCalculatedInfo,
  symbol: string,
  side: 'buy' | 'sell',
  stepSizeDecimals?: number
) => {
  const { notAvailable, quantityBuy, quantitySell } = calculatedInfo;
  const quantity = side === 'buy' ? quantityBuy : quantitySell;
  return notAvailable
    ? 'N/A'
    : quantity
    ? `${formatNumber(quantity, {
        fixedDecimals: stepSizeDecimals,
      })} ${symbol}`
    : '-';
};

const formatCalculatedCost = (
  calculatedInfo: BuySellPanelCalculatedInfo,
  side: 'buy' | 'sell'
) => {
  const { costSell, costBuy, notAvailable } = calculatedInfo;
  const cost = side === 'buy' ? costBuy : costSell;
  return notAvailable
    ? 'N/A'
    : cost || cost === 0
    ? formatUsdWithSign(cost)
    : undefined;
};

const formatCalculatedValue = (
  calculatedInfo: BuySellPanelCalculatedInfo,
  side: 'buy' | 'sell'
) => {
  const { valueBuy, valueSell, notAvailable } = calculatedInfo;
  const value = side === 'buy' ? valueBuy : valueSell;
  return notAvailable ? 'N/A' : value ? formatUsdWithSign(value) : undefined;
};

export function BuySellPanelInfoQuantities({
  token1base,
  token2quote,
  calculatedInfo,
}: {
  token1base: string;
  token2quote: string;
  calculatedInfo: BuySellPanelCalculatedInfo;
}) {
  const stepSize = useSelectMarketStepSize(`${token1base}-${token2quote}`);
  return (
    <TwoColumnData
      label="Quantity"
      buySide={formatCalculatedQuantity(
        calculatedInfo,
        token1base,
        'buy',
        stepSize?.visibleDecimals
      )}
      sellSide={formatCalculatedQuantity(
        calculatedInfo,
        token1base,
        'sell',
        stepSize?.visibleDecimals
      )}
    />
  );
}

const StrikeThroughFees = styled.span`
  text-decoration: line-through;
  color: var(--gray4c);
  font-family: var(--digitsFontFamily);
`;

const DiscountedFees = styled.span`
  font-family: var(--digitsFontFamily);
`;

// const FeesValuesStack = styled.div`
//   display: flex;
//   flex-direction: column;
//   align-items: flex-end;
//   gap: 2px;
// `;

const formatFee = (v: number | undefined) =>
  v || v === 0 ? `${formatNumber2(v, 4, 2)}%` : '';

export function TakerMakerFeesTwoColumnData({
  takerFee,
  makerFee,
  baseTakerFee,
  baseMakerFee,
}: {
  takerFee: number | undefined;
  makerFee: number | undefined;
  baseTakerFee: number | undefined;
  baseMakerFee: number | undefined;
}) {
  const isMobile = useSelector(state => state.screen.isMobile);
  const walletAddress = useSelector(state => state.wallet.walletAddress);

  // When the wallet is not unlocked we only show the default (override) fee,
  // never the per-market fee that could otherwise look like a discount.
  // The discounted view is reserved for unlocked wallets.
  const displayTakerFee = walletAddress ? takerFee : baseTakerFee;
  const displayMakerFee = walletAddress ? makerFee : baseMakerFee;

  // While unlocked but the wallet-specific fee is still loading we don't yet
  // know whether a discount applies, so reserve the extra row to avoid jumping.
  const isLoadingDiscount =
    !!walletAddress && (takerFee === undefined || makerFee === undefined);

  const hasDiscount =
    !!walletAddress &&
    baseTakerFee !== undefined &&
    baseMakerFee !== undefined &&
    takerFee !== undefined &&
    makerFee !== undefined &&
    (baseTakerFee > takerFee || baseMakerFee > makerFee);

  const baseFees = (
    <>
      {formatFee(baseTakerFee)} / {formatFee(baseMakerFee)}
    </>
  );

  const currentFees = (
    <>
      {formatFee(displayTakerFee)} / {formatFee(displayMakerFee)}
    </>
  );

  if (isMobile) {
    return (
      <DataPair label="Fees">
        {isLoadingDiscount ? (
          <DiscountedFees>&nbsp;</DiscountedFees>
        ) : hasDiscount ? (
          <DiscountedFees>🔥 {currentFees}</DiscountedFees>
        ) : (
          <>{currentFees}</>
        )}
        {/* Double-line but problematic because not enough vertical space: <FeesValuesStack>
          {hasDiscount ? (
            <>
              <StrikeThroughFees>{baseFees}</StrikeThroughFees>
              <DiscountedFees>🔥 {currentFees}</DiscountedFees>
            </>
          ) : isLoadingDiscount ? (
            // Reserve the struck-fee line height only while the discount is still loading.
            <>
              <StrikeThroughFees>&nbsp;</StrikeThroughFees>
              <DiscountedFees>{currentFees}</DiscountedFees>
            </>
          ) : (
            <>{currentFees}</>
          )}
        </FeesValuesStack> */}
      </DataPair>
    );
  }
  return (
    <>
      <TwoColumnDataSimple
        label="Taker / Maker Fee"
        rightSide={
          hasDiscount ? (
            <StrikeThroughFees>{baseFees}</StrikeThroughFees>
          ) : (
            currentFees
          )
        }
      />
      {/* Discount/reserved space to prevent market switch jump */}
      {(hasDiscount || isLoadingDiscount) && (
        <TwoColumnDataSimple
          label=""
          rightSide={isLoadingDiscount ? <>&nbsp;</> : <>🔥 {currentFees}</>}
        />
      )}
    </>
  );
}

export function BuySellPanelInfoBottom({
  calculatedInfo,
  takerFee,
  makerFee,
  baseTakerFee,
  baseMakerFee,
}: {
  calculatedInfo: BuySellPanelCalculatedInfo;
  takerFee: number | undefined;
  makerFee: number | undefined;
  baseTakerFee: number | undefined;
  baseMakerFee: number | undefined;
}) {
  return (
    <div
      css={css`
        position: relative;
      `}
    >
      {calculatedInfo.revalidating ? (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            opacity: 0.2,
            background: 'var(--black1b)',
          }}
        />
      ) : null}
      <TwoColumnData
        label="Position Margin"
        buySide={formatCalculatedCost(calculatedInfo, 'buy')}
        sellSide={formatCalculatedCost(calculatedInfo, 'sell')}
      />
      <TwoColumnData
        label="Value"
        buySide={formatCalculatedValue(calculatedInfo, 'buy')}
        sellSide={formatCalculatedValue(calculatedInfo, 'sell')}
      />
      <TakerMakerFeesTwoColumnData
        makerFee={makerFee}
        takerFee={takerFee}
        baseMakerFee={baseMakerFee}
        baseTakerFee={baseTakerFee}
      />
    </div>
  );
}

// fees are set on three scopes:
//  - exchange Get Exchange
//  - market Get Market
//  - wallet Get Wallet
// and the trading engine applies the lowest fee between the three.

export function MobilePanelInfo({
  calculatedInfo,
  side,
  token1base,
  token2quote,
}: {
  calculatedInfo: BuySellPanelCalculatedInfo;
  side: 'buy' | 'sell';
  token1base: string;
  token2quote: string;
}) {
  const stepSize = useSelectMarketStepSize(`${token1base}-${token2quote}`);
  return (
    <>
      <DataPair label={capitalizeFirstLetter(side)}>
        {formatCalculatedQuantity(
          calculatedInfo,
          token1base,
          side,
          stepSize?.visibleDecimals
        )}
      </DataPair>
      <DataPair label="Position Margin">
        {formatCalculatedCost(calculatedInfo, side)}
      </DataPair>
      <DataPair label="Value">
        {formatCalculatedValue(calculatedInfo, side)}
      </DataPair>

      {/* <DataPair label="Taker / Maker Fee">
        {formatPercentage(takerFee)}/{formatPercentage(makerFee)}
      </DataPair> */}
    </>
  );
}
